import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Skeleton } from './ui/skeleton';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import {
  FileText,
  Upload,
  Clock,
  CheckCircle2,
  AlertCircle,
  RefreshCcw,
  ChevronRight,
  Search,
  Info,
  ExternalLink,
  FileCheck,
  XCircle,
} from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format relative time
const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  
  return date.toLocaleDateString();
};

// Status badge component with consistent styling
const OCRStatusBadge = ({ status }) => {
  const config = {
    pending: {
      label: 'Processing',
      className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      icon: RefreshCcw,
    },
    processing: {
      label: 'Processing',
      className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      icon: RefreshCcw,
    },
    requires_review: {
      label: 'Needs Review',
      className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
      icon: AlertCircle,
    },
    completed: {
      label: 'Ready',
      className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
      icon: CheckCircle2,
    },
    failed: {
      label: 'Failed',
      className: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400',
      icon: XCircle,
    },
  };
  
  const { label, className, icon: Icon } = config[status] || config.pending;
  
  return (
    <Badge variant="secondary" className={cn('text-xs gap-1', className)}>
      <Icon className="w-3 h-3" />
      {label}
    </Badge>
  );
};

// Get action button config based on job status
const getActionConfig = (job) => {
  if (job.draft_created && job.draft_id) {
    return {
      label: 'View Draft',
      variant: 'ghost',
      icon: ExternalLink,
      action: 'view_draft',
    };
  }
  
  switch (job.status) {
    case 'requires_review':
      return {
        label: 'Resume',
        variant: 'default',
        icon: ChevronRight,
        action: 'resume',
      };
    case 'completed':
      return {
        label: 'Review',
        variant: 'default',
        icon: FileCheck,
        action: 'review',
      };
    case 'failed':
      return {
        label: 'Retry',
        variant: 'outline',
        icon: RefreshCcw,
        action: 'retry',
      };
    case 'pending':
    case 'processing':
      return {
        label: 'Processing...',
        variant: 'ghost',
        icon: RefreshCcw,
        action: 'processing',
        disabled: true,
      };
    default:
      return {
        label: 'View',
        variant: 'ghost',
        icon: ChevronRight,
        action: 'view',
      };
  }
};

// Single job row
const OCRJobRow = ({ job, documentType, onAction, compact = false }) => {
  const actionConfig = getActionConfig(job);
  
  return (
    <div 
      className={cn(
        "flex items-center justify-between gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors",
        compact && "py-2"
      )}
      data-testid={`ocr-job-${job.job_id}`}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="p-2 bg-muted rounded-lg flex-shrink-0">
          <FileText className="w-4 h-4 text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate" title={job.filename}>
            {job.filename}
          </p>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {job.extracted_name && (
              <>
                <span className="truncate max-w-[120px]" title={job.extracted_name}>
                  {job.extracted_name}
                </span>
                <span>•</span>
              </>
            )}
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatRelativeTime(job.uploaded_at)}
            </span>
          </div>
        </div>
      </div>
      
      <div className="flex items-center gap-2 flex-shrink-0">
        <OCRStatusBadge status={job.draft_created ? 'completed' : job.status} />
        <Button
          variant={actionConfig.variant}
          size="sm"
          className="h-8"
          disabled={actionConfig.disabled}
          onClick={() => onAction(job, actionConfig.action)}
          data-testid={`ocr-job-action-${job.job_id}`}
        >
          <actionConfig.icon className={cn("w-3.5 h-3.5", !compact && "mr-1", actionConfig.disabled && "animate-spin")} />
          {!compact && actionConfig.label}
        </Button>
      </div>
    </div>
  );
};

// Filter chips for View All drawer
const StatusFilterChips = ({ selected, onChange }) => {
  const statuses = [
    { value: 'all', label: 'All' },
    { value: 'requires_review', label: 'Needs Review' },
    { value: 'completed', label: 'Ready' },
    { value: 'failed', label: 'Failed' },
  ];
  
  return (
    <div className="flex flex-wrap gap-2">
      {statuses.map(status => (
        <Button
          key={status.value}
          variant={selected === status.value ? 'default' : 'outline'}
          size="sm"
          className="h-7 text-xs"
          onClick={() => onChange(status.value)}
          data-testid={`filter-${status.value}`}
        >
          {status.label}
        </Button>
      ))}
    </div>
  );
};

// View All Drawer
const ViewAllDrawer = ({ open, onOpenChange, documentType, jobs, loading, onAction, onSearch, onFilterChange, searchQuery, statusFilter, total }) => {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[450px] sm:w-[500px]" data-testid="ocr-jobs-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            OCR Uploads
          </SheetTitle>
          <SheetDescription>
            {documentType === 'bill' ? 'Bill' : 'Invoice'} documents uploaded for OCR processing
          </SheetDescription>
        </SheetHeader>
        
        <div className="mt-6 space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by filename or vendor..."
              value={searchQuery}
              onChange={(e) => onSearch(e.target.value)}
              className="pl-9"
              data-testid="ocr-search-input"
            />
          </div>
          
          {/* Filters */}
          <StatusFilterChips selected={statusFilter} onChange={onFilterChange} />
          
          <Separator />
          
          {/* Job List */}
          <ScrollArea className="h-[calc(100vh-280px)]">
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : jobs.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-10 h-10 mx-auto mb-3 text-muted-foreground/50" />
                <p className="text-muted-foreground">No OCR uploads found</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {statusFilter !== 'all' ? 'Try changing the filter' : 'Upload a document to get started'}
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                {jobs.map(job => (
                  <OCRJobRow
                    key={job.job_id}
                    job={job}
                    documentType={documentType}
                    onAction={onAction}
                  />
                ))}
              </div>
            )}
          </ScrollArea>
          
          {/* Total count */}
          {total > 0 && (
            <p className="text-xs text-muted-foreground text-center">
              Showing {jobs.length} of {total} uploads
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

// Main component
export default function RecentOCRUploads({ documentType = 'bill' }) {
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const cardRef = useRef(null);
  
  const [jobs, setJobs] = useState([]);
  const [allJobs, setAllJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [totalJobs, setTotalJobs] = useState(0);
  const [drawerLoading, setDrawerLoading] = useState(false);
  
  // Fetch recent jobs for the card
  const fetchRecentJobs = useCallback(async () => {
    if (!currentWorkspace) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/ocr/jobs/list?workspace_id=${currentWorkspace.workspace_id}&document_type=${documentType}&limit=5`
      );
      
      if (response.ok) {
        const data = await response.json();
        setJobs(data.jobs || []);
        setTotalJobs(data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch OCR jobs:', error);
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace, documentType]);
  
  // Fetch all jobs for the drawer
  const fetchAllJobs = useCallback(async () => {
    if (!currentWorkspace) return;
    
    setDrawerLoading(true);
    try {
      let url = `${API_URL}/api/ocr/jobs/list?workspace_id=${currentWorkspace.workspace_id}&document_type=${documentType}&limit=50`;
      
      if (statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }
      
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
        setAllJobs(data.jobs || []);
      }
    } catch (error) {
      console.error('Failed to fetch all OCR jobs:', error);
    } finally {
      setDrawerLoading(false);
    }
  }, [currentWorkspace, documentType, statusFilter, searchQuery]);
  
  // Initial fetch
  useEffect(() => {
    fetchRecentJobs();
  }, [fetchRecentJobs]);
  
  // Fetch all when drawer opens or filters change
  useEffect(() => {
    if (drawerOpen) {
      fetchAllJobs();
    }
  }, [drawerOpen, fetchAllJobs]);
  
  // Handle job actions
  const handleAction = (job, action) => {
    switch (action) {
      case 'resume':
      case 'review':
      case 'view':
        navigate(`/invoice-ocr?type=${documentType}&job=${job.job_id}`);
        break;
      case 'view_draft':
        if (job.draft_type === 'bill') {
          navigate('/bills');
        } else {
          navigate('/invoices');
        }
        break;
      case 'retry':
        // Re-open the OCR page to retry upload
        navigate(`/invoice-ocr?type=${documentType}`);
        break;
      default:
        break;
    }
    setDrawerOpen(false);
  };
  
  // Scroll to card (for toast link)
  const scrollToCard = () => {
    cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  
  // Expose scroll function globally for toast
  useEffect(() => {
    window.scrollToOCRUploads = scrollToCard;
    return () => {
      delete window.scrollToOCRUploads;
    };
  }, []);
  
  // Don't render anything if no jobs and not loading
  if (!loading && jobs.length === 0) {
    return null;
  }
  
  return (
    <>
      <Card ref={cardRef} className="mb-6" data-testid="recent-ocr-uploads-card">
        <CardHeader className="py-3 px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Upload className="w-4 h-4 text-muted-foreground" />
              <CardTitle className="text-sm font-medium">Recent OCR Uploads</CardTitle>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-5 w-5">
                      <Info className="w-3.5 h-3.5 text-muted-foreground" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-[250px]">
                    <p className="text-xs">
                      Uploading a document creates an OCR job. Jobs remain until a draft is created or deleted. Nothing posts automatically.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => setDrawerOpen(true)}
              data-testid="view-all-ocr-btn"
            >
              View All
              <ChevronRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="py-0 px-2 pb-2">
          {loading ? (
            <div className="space-y-2 px-2">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : (
            <div className="divide-y divide-border/50">
              {jobs.slice(0, 5).map(job => (
                <OCRJobRow
                  key={job.job_id}
                  job={job}
                  documentType={documentType}
                  onAction={handleAction}
                  compact
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* View All Drawer */}
      <ViewAllDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        documentType={documentType}
        jobs={allJobs}
        loading={drawerLoading}
        onAction={handleAction}
        onSearch={setSearchQuery}
        onFilterChange={setStatusFilter}
        searchQuery={searchQuery}
        statusFilter={statusFilter}
        total={totalJobs}
      />
    </>
  );
}

// Export a utility to show toast after upload
export const showOCRUploadToast = (toast, documentType) => {
  toast.success(
    <div className="flex flex-col gap-1">
      <span>{documentType === 'bill' ? 'Bill' : 'Invoice'} uploaded. OCR job started.</span>
      <button
        className="text-xs text-primary underline text-left"
        onClick={() => {
          if (window.scrollToOCRUploads) {
            window.scrollToOCRUploads();
          }
        }}
      >
        View uploads →
      </button>
    </div>,
    { duration: 5000 }
  );
};
